import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import { formatUrl } from '@/lib/utils';

interface CreateOrganizationDialogProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateOrganizationDialog({
  trigger,
  onSuccess,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: CreateOrganizationDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;

  const [organizationName, setOrganizationName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createOrganization } = useOrganization();

  const reset = () => {
    setOrganizationName('');
    setCompanyUrl('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const trimmedName = organizationName.trim();
  const canSubmit = trimmedName.length > 0 && !isCreating;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setIsCreating(true);
      await createOrganization(
        trimmedName,
        companyUrl.trim() ? formatUrl(companyUrl.trim()) : undefined,
      );
      toast({
        title: 'Organization created',
        description: `"${trimmedName}" is ready to go.`,
      });
      reset();
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        title: 'Error',
        description: 'Failed to create organization. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const body = (
    <DialogContent className="sm:max-w-[420px]">
      <DialogHeader>
        <DialogTitle className="inline-flex items-center text-base">
          Create Organization
          <HelpTooltip text="Add your company URL and Hobbes will use it to personalize your agents." />
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} noValidate className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="organization-name" className="text-sm font-medium">
            Name
          </Label>
          <Input
            id="organization-name"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Acme Inc"
            disabled={isCreating}
            autoFocus
          />
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="organization-url" className="text-sm font-medium">
            Company URL <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="organization-url"
            type="url"
            value={companyUrl}
            onChange={(e) => setCompanyUrl(e.target.value)}
            placeholder="https://yourcompany.com"
            disabled={isCreating}
          />
        </div>

        <DialogFooter className="mt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );

  if (isControlled) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {body}
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Organization
          </Button>
        )}
      </DialogTrigger>
      {body}
    </Dialog>
  );
}
